import { useEffect, useRef } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import useAuth from '@/hooks/useAuth.ts';
import LADDER_PROFILE from '@/graphql/queries/ladder-profile.graphql';
import LADDER_VERSION from '@/graphql/queries/ladder-version.graphql';

export type MatchMode = 'FRIENDLY' | 'LADDER' | 'TOURNAMENT';
export type MatchStatus = 'ACTIVE' | 'CANCELLED' | 'COMPLETED' | 'DECLINED' | 'EXPIRED' | 'PENDING' | 'WITHDRAWN';
export type LadderPool = 'SOLO' | 'H2H_R13' | 'H2H_R4' | 'H2H_R5';

export interface PublicPlayer {
  clientId: number;
  ladderName: string;
  avatarIdx: number;
  avatarBackgroundIdx: number;
  rank: number | null;
  wins: number;
  losses: number;
  streak: number;
  daysIdle: number | null;
}

export interface LadderMatchRound {
  ladderMatchRoundId: number;
  roundNumber: number;
  examQuestionId: number;
  resolved: boolean;
  roundWinner: 'CHALLENGER' | 'OPPONENT' | 'NONE' | null;
  challengerIsCorrect: boolean | null;
  challengerElapsedMs: number | null;
  opponentIsCorrect: boolean | null;
  opponentElapsedMs: number | null;
  timedOutClientId: number | null;
}

export interface LadderMatch {
  ladderMatchId: number;
  matchMode: MatchMode;
  matchStatus: MatchStatus;
  endedReason: string | null;
  endTitle: string | null;
  endMessage: string | null;
  challengerId: number;
  opponentId: number;
  challengerScore: number;
  opponentScore: number;
  currentRound: number;
  currentTurnClientId: number | null;
  turnDeadlineAt: string | null;
  climbIfWin: number | null;
  isRankWar: boolean;
  tournamentId: number | null;
  tournamentRound: number | null;
  setExamId: number | null;
  r4ExamId: number | null;
  r5ExamId: number | null;
  winnerId: number | null;
  completedAt: string | null;
  updated: string;
  challenger: PublicPlayer | null;
  opponent: PublicPlayer | null;
  rounds: LadderMatchRound[];
}

export interface LadderVaultItem {
  clientExamQuestionId: number;
  examId: number;
  clientExamId: number;
  examComponentId: number;
  examQuestionId: number;
  position: number;
  label: string;
  pool: LadderPool;
  correct: boolean;
  playedAt: string;
}

export interface LadderStats {
  rank: number | null;
  rankDelta: number | null;
  rankedPlayers: number;
  wins: number;
  losses: number;
  streak: number;
  daysIdle: number | null;
  activeMatches: number;
  pendingChallenges: number;
}

export interface LadderProfile {
  clientId: number;
  ladderName: string;
  avatarIdx: number;
  avatarBackgroundIdx: number;
}

/**
 * Matches are played in the app, so this page only reflects them. The backend exposes a cheap
 * change marker for exactly this: poll it and refetch whatever is on screen only when it moves.
 */
export const useLadderLiveUpdates = () => {
  const client = useApolloClient();
  const lastVersion = useRef<string>();
  const { data } = useQuery(LADDER_VERSION, { pollInterval: 30_000, fetchPolicy: 'network-only' });
  const version: string | undefined = data?.ladderMyVersion;

  useEffect(() => {
    if (!version) return;
    if (lastVersion.current && lastVersion.current !== version) {
      client.refetchQueries({ include: 'active' });
    }
    lastVersion.current = version;
  }, [client, version]);
};

export const useLadderProfile = () => {
  const { data, loading } = useQuery(LADDER_PROFILE);

  return {
    loading,
    profile: data?.user as LadderProfile | undefined,
    stats: data?.stats as LadderStats | undefined,
  };
};

export const useMyClientId = () => Number(useAuth().clientId);

/** The viewer's side of a head-to-head match, whichever of the two players they are. */
export const getMatchSides = (match: LadderMatch, myId: number) => {
  const isChallenger = match.challengerId === myId;

  return {
    me: isChallenger ? match.challenger : match.opponent,
    them: isChallenger ? match.opponent : match.challenger,
    myScore: isChallenger ? match.challengerScore : match.opponentScore,
    theirScore: isChallenger ? match.opponentScore : match.challengerScore,
    isMyTurn: match.matchStatus === 'ACTIVE' && match.currentTurnClientId === myId,
    rounds: match.rounds.map(round => ({
      roundNumber: round.roundNumber,
      examQuestionId: round.examQuestionId,
      resolved: round.resolved,
      won: round.roundWinner === (isChallenger ? 'CHALLENGER' : 'OPPONENT'),
      lost: round.roundWinner === (isChallenger ? 'OPPONENT' : 'CHALLENGER'),
      myCorrect: isChallenger ? round.challengerIsCorrect : round.opponentIsCorrect,
      myElapsedMs: isChallenger ? round.challengerElapsedMs : round.opponentElapsedMs,
      theirCorrect: isChallenger ? round.opponentIsCorrect : round.challengerIsCorrect,
      theirElapsedMs: isChallenger ? round.opponentElapsedMs : round.challengerElapsedMs,
      timedOut: round.timedOutClientId === myId,
    })),
  };
};

export const MATCH_MODE_LABELS: Record<MatchMode, string> = {
  LADDER: 'Ranked',
  FRIENDLY: 'Friendly',
  TOURNAMENT: 'Tournament',
};

export const POOL_LABELS: Record<LadderPool, string> = {
  SOLO: 'Solo',
  H2H_R13: 'Head-to-head',
  H2H_R4: 'Head-to-head',
  H2H_R5: 'Head-to-head',
};

// `NaiveDateTime` values come without an offset and are UTC on the server.
const toDateTime = (value: string) => {
  const naive = DateTime.fromISO(value, { zone: 'utc' });
  return naive.isValid ? naive.toLocal() : DateTime.fromISO(value);
};

export const formatDate = (value?: string | null) =>
  value ? toDateTime(value).toLocaleString(DateTime.DATE_MED) : '—';

export const formatRelative = (value?: string | null) =>
  value ? toDateTime(value).toRelative() ?? '' : '';

export const formatSeconds = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export const formatMs = (ms?: number | null) =>
  ms === null || ms === undefined ? '—' : formatSeconds(Math.round(ms / 1000));

export const formatPercent = (part: number, total: number) =>
  total ? `${Math.round((part / total) * 100)}%` : '—';

export const formatStreak = (streak: number) => {
  if (streak > 0) return `${streak} win streak`;
  if (streak < 0) return `${-streak} loss streak`;
  return '—';
};

export const getReviewPath = (item: LadderVaultItem) =>
  `/review/${item.examId}/${item.clientExamId}/${item.examComponentId}/${item.position}`;

export type Tone = 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'neutral';

export const TONE_TEXT: Record<Tone, string> = {
  success: 'text-success-neutral-text-softer',
  danger: 'text-danger-neutral-text-softer',
  warning: 'text-warning',
  info: 'text-info-neutral-text-softer',
  primary: 'text-primary',
  neutral: 'text-neutral2-text-softer',
};

// Ranks drop after 7 idle days; the app warns a day before.
export const getIdleTone = (daysIdle: number | null): Tone => {
  if (daysIdle === null) return 'neutral';
  if (daysIdle >= 6) return 'danger';
  if (daysIdle >= 4) return 'warning';
  return 'success';
};

export interface SoloSet {
  position: number;
  played: boolean;
  playable: boolean;
  unlocked: boolean;
  won: boolean;
  answers: boolean[];
  roundsWon: boolean[];
  exam: { examId: number; name: string };
  clientExam: { clientExamId: number; updated: string } | null;
}

export interface SoloSets {
  played: number;
  won: number;
  lost: number;
  rightAnswers: number;
  wrongAnswers: number;
  sets: SoloSet[];
}

export const getSoloResult = (set: SoloSet): { label: string; tone: Tone } => {
  const roundsWon = set.roundsWon.filter(Boolean).length;

  if (!set.played) return { label: set.answers.length ? 'In progress' : 'Not played', tone: 'neutral' };
  if (set.won && roundsWon === set.roundsWon.length) return { label: 'Sweep', tone: 'warning' };
  if (set.won) return { label: 'Win', tone: 'success' };
  return { label: 'Defeat', tone: 'danger' };
};

export interface VaultCategory {
  questionCategoryId: number;
  name: string;
  answered: number;
  correct: number;
  items: LadderVaultItem[];
}

export interface VaultGroup {
  name: string;
  studycenterGroupId: number | null;
  answered: number;
  correct: number;
  categories: VaultCategory[];
}
